import React, {useCallback, useRef, useState} from 'react'
import {css} from '@emotion/core'
import styled from '@emotion/styled'

import {isAuth0PhoneNumberFallbackPicture} from '@daedalus/core/src/auth/business/authentication'
import {FormattedMessageWrapper} from '@daedalus/core/src/localization/components/FormattedMessage'
import {getUserName} from '@daedalus/core/src/user/business'

import Avatar, {AvatarType} from '../../../../designSystem/Avatar'
import Button from '../../../../designSystem/Button'
import SignInButton from '../../SignInButton/BSide'
import {
  PlusIcon,
  PlusIconWrapper
} from '../../../privateDeals/PrivateDealTag/PlusIcon'
import {Popover, PopoverPlacement} from '../../../search/Popover'

import {
  ShowPasswordlessLoginPayload,
  User
} from '@daedalus/core/src/auth/types/Auth'
import PopoverMenuLink from '../../../search/Popover/PopoverMenuLink'

interface Props {
  /** Whether the user is authenticated */
  isAuthenticated: boolean
  /** Callback fired when the user tries to sign in */
  onSignIn: ({
    sourceComponent,
    flow
  }: {
    sourceComponent: ShowPasswordlessLoginPayload['sourceComponent']
    flow: ShowPasswordlessLoginPayload['flow']
  }) => void
  /** User object with metadata such as profile picture, name etc. */
  user: User | null | undefined
  /** Callback fired when the user tries to log out */
  logOut?: () => void
  /** Url of users account page */
  accountUrl?: string
  /** Whether the user is Plus user */
  isMemberPlus: boolean
}

const AVATAR_SIZE = 32

const StyledButton = styled(Button)(
  ({theme}) => css`
    display: flex;
    align-items: center;
    &:focus:not(:hover) {
      background: ${theme.colors.defaultBg};
    }
  `
)

const AvatarWrapper = styled('div')<{isMemberPlus: boolean}>(
  ({theme, isMemberPlus}) => css`
    width: 40px;
    height: 40px;
    box-sizing: border-box;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: ${isMemberPlus ? `${theme.layout.spacing.s200}px` : '0'};
    box-shadow: ${isMemberPlus
      ? `inset 0 0 0 1px ${theme.colors.violet.v300}`
      : 'none'};
    border-radius: 50%;
    position: relative;
  `
)

const MessageWrapper = styled('span')(
  ({theme}) => css`
    margin-left: ${theme.layout.spacing.s300}px;
  `
)

const UserWidget = ({
  isAuthenticated,
  onSignIn,
  user,
  logOut,
  accountUrl,
  isMemberPlus
}: Props) => {
  const [popoverOpen, setPopoverOpen] = useState(false)

  const togglePopover = useCallback(() => {
    setPopoverOpen(!popoverOpen)
  }, [popoverOpen, setPopoverOpen])

  const anchorRef = useRef(null)

  const handleLogOutClick = useCallback(() => {
    if (logOut) logOut()
  }, [logOut])

  const AuthenticatedMenu = () => (
    <>
      <PopoverMenuLink url={accountUrl}>
        <FormattedMessageWrapper
          dataId="AccountProfileLink"
          id="manageAccount"
          defaultMessage="Manage account"
        />
      </PopoverMenuLink>
      <PopoverMenuLink onClick={handleLogOutClick}>
        <FormattedMessageWrapper
          dataId="LogOutProfileLink"
          id="userWidget.signOut"
          defaultMessage="Sign out"
        />
      </PopoverMenuLink>
    </>
  )

  return isAuthenticated ? (
    <>
      <StyledButton
        ref={anchorRef}
        appearance="buttonLink"
        dataId="UserWidget"
        className="fs-exclude"
        onMouseDown={togglePopover}
        onKeyDown={togglePopover}
      >
        <AvatarWrapper isMemberPlus={isMemberPlus}>
          <Avatar
            picture={user?.picture}
            name={getUserName(user)}
            type={
              isAuth0PhoneNumberFallbackPicture(user?.picture)
                ? AvatarType.Icon
                : AvatarType.Picture
            }
            size={AVATAR_SIZE}
            iconSize={20}
          />
          {isMemberPlus && (
            <PlusIconWrapper>
              <PlusIcon variant="text" size="xs" translateY="0" />
            </PlusIconWrapper>
          )}
        </AvatarWrapper>

        <MessageWrapper>
          {user?.given_name ? (
            user.given_name
          ) : (
            <FormattedMessageWrapper
              id="userWidget.myProfile"
              defaultMessage="My profile"
            />
          )}
        </MessageWrapper>
      </StyledButton>
      <Popover
        isOpen={popoverOpen}
        content={<AuthenticatedMenu />}
        anchorRef={anchorRef}
        placement={PopoverPlacement.Bottom}
        onOutsideClick={togglePopover}
      />
    </>
  ) : (
    <SignInButton onSignIn={onSignIn} />
  )
}

export default UserWidget
